import { useMutation } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { CacheFactory } from '../../../models/CacheFactory'
import { Field } from '../../../models/Field'
import { PSRecord } from '../../../models/PSRecord'
import { RecordDataMap } from '../../../models/RecordDataMap'
import { RecordFactory } from '../../../models/RecordFactory'
import { Type } from '../../../models/Type'
import { PSApplication } from '../../app/PSApplication'

export const NPSContext = createContext<NPSRecordState | undefined>(undefined)

export function useNPSContext() {
    const state = useContext(NPSContext)

    if (!state) {
        throw new Error(`${useNPSContext.name} called in tree without context provider.`)
    }

    return state
}

export type NPSViewModelProps = {
    form: Type
    fields: Field[]
    initialRecord?: PSRecord
}

export type NPSRecordState = {
    record: PSRecord
    setField: (fieldId: string, value: unknown) => PSRecord
    /**
     * Takes an object having field ids as its keys and sets the corresponding values on the record.
     */
    setValues: (data: Record<string, unknown>) => PSRecord
    save: (override?: PSRecord) => Promise<PSRecord>
    isSaving: boolean

    getField: (fieldId: string) => Field | undefined
    getFieldValue: (fieldId: string) => unknown | undefined
    getFieldValues: () => Record<string, unknown>
}

/**
 * Manages {@link PSRecord} as the backing model for form UIs.
 * API is given in terms of field ids rather than names, which are brittle to upstream changes.
 */
export function NPSViewModel({
    form,
    fields,
    initialRecord,
}: NPSViewModelProps): NPSRecordState {
    const [record, setRecord] = useState(() => {
        let initialState
        if (initialRecord) {
            initialState = initialRecord
        }
        else {
            const user = PSApplication.me()

            initialState = PSRecord.initWithType(form)
            initialState.person = user
        }

        return initialState
    })

    const fieldValues = useMemo(() => record.fieldValues.serialize(), [record])

    const fieldValuesById = useMemo(
        () =>
            fields.reduce((acc: Record<string, unknown>, field: Field) => {
                acc[field.id!] = record.fieldValues.get(field.name!)

                return acc
            }, {}),
        [fields, record]
    )

    const getField = (fieldId: string) => {
        return fields.find((field) => field.id === fieldId)
    }

    const getFieldValue = (fieldId: string) => {
        return fieldValuesById[fieldId]
    }

    const getFieldValues = () => fieldValuesById

    const setField = (id: string, value: unknown) => {
        const field = getField(id)

        if (!field) {
            return record
        }

        const next = record.clone({
            field_values: new RecordDataMap({
                ...fieldValues,
                [field.name!]: value,
            }),
        })

        setRecord(next)

        return next
    }

    const setValues = (data: Record<string, unknown>) => {
        const next = record.clone({
            field_values: new RecordDataMap({
                ...fieldValues,
                ...Object.entries(data).reduce((acc: Record<string, unknown>, [id, value]) => {
                    const field = getField(id)

                    if (!field) {
                        return acc
                    }

                    acc[field.name!] = value

                    return acc
                }, {}),
            }),
        })

        setRecord(next)

        return next
    }

    const { mutateAsync: saveRecord, isLoading: isSaving } = useMutation({
        mutationFn: async (record: PSRecord) => {
            // TODO Allow configuration of sendRecord params?
            const result = await RecordFactory.getInstance().sendRecord(record, false, true)

            if (!result.ok) {
                throw new Error(`${result.error}`)
            }

            return result.value
        },
    })

    const save = async (override?: PSRecord) => {
        const result = await saveRecord(override ?? record)

        setRecord(result)

        return result
    }

    // Some fields must be present in the cache for their records to be serialized correctly.
    // Here we just store them all. Note: strictly speaking, this should be ensured prior to every
    // possible serialization event, but we assume eviction does not occur during this hook's lifecycle.
    useEffect(() => {
        fields.forEach((field) => {
            const ownerId = form.owner?.id

            if (!field.id || !ownerId || !field.type) {
                return
            }

            CacheFactory.getInstance().addObject(
                Field.cacheKey(field.id, ownerId, form.id),
                field.type
            )
        })
    }, [fields, form])

    return {
        record,
        setField,
        setValues,
        getField,
        getFieldValue,
        getFieldValues,
        save,
        isSaving,
    }
}
